'use client';

import React, { useState } from 'react';
import { Route } from 'next';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { GeoforgeLogoWithText } from '@/components/icons/geoforge-logo-with-text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUrlWithReturnTo } from '@/hooks/use-url-with-return-to';
import { signUp } from '@/app/actions/auth';
import { APP_SETTINGS } from '@/lib/config';
import { initializeUserInStore } from '@/lib/init-user-utils';
import { CAPTCHA_FAILED_MESSAGE } from '@/utils/auth/captcha';
import {
  buildUrlWithReturnTo,
  getSafeRedirectPath,
  withEmailParam,
} from '@/utils/redirect-validation';
import { isValidRedirectPath } from '@/utils/redirect-validation';
import { AuthDivider } from './auth-divider';
import { CaptchaWidget, useCaptcha } from './captcha';
import { GoogleAuthButton } from './google-auth-button';
import { PasswordInput } from './password-input';
import { SignupErrorMessage } from './signup-error-message';

const SignUpForm = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>('Loading...');
  const router = useRouter();
  const searchParams = useSearchParams();
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const { buildUrlWithReturnToParam } = useUrlWithReturnTo();
  const emailParam = searchParams.get('email');
  const { widgetRef, attachCaptchaToken, resetCaptcha } = useCaptcha();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setLoadingText('Creating account...');
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      setLoadingText('Loading...');
      return;
    }

    const formData = new FormData(event.currentTarget);

    const returnTo = searchParams.get('returnTo');
    if (returnTo) {
      formData.set('returnTo', returnTo);
    }

    if (!(await attachCaptchaToken(formData))) {
      setError(CAPTCHA_FAILED_MESSAGE);
      setLoading(false);
      setLoadingText('Loading...');
      return;
    }

    const result = await signUp(formData);

    if (result.isSuccess) {
      setError(null);
      setLoadingText('Redirecting...');

      const returnTo = searchParams.get('returnTo');
      if (result.data?.isInvitationSignup && returnTo) {
        const decodedPath = decodeURIComponent(returnTo);
        if (decodedPath.startsWith('/invitation/') && isValidRedirectPath(decodedPath)) {
          router.push(decodedPath as Route);
          return;
        }
      }

      // Signup produced a live session (email confirmation disabled) — resume the
      // interrupted flow directly instead of bouncing through /login.
      if (result.data?.hasSession) {
        await initializeUserInStore();
        toast.success('Account created!');
        router.push(getSafeRedirectPath(returnTo));
        return;
      }

      const emailForLogin = (formData.get('email') as string) || emailParam;
      const loginUrl = withEmailParam(buildUrlWithReturnTo('/login', returnTo), emailForLogin);
      router.push(loginUrl);

      if (result.data?.isInvitationSignup) {
        toast.success('Account created and verified! You can now sign in to join the workspace.', {
          duration: 8000,
        });
      } else {
        toast.success(
          'Account created! Please check your email and click the link to verify your account.',
          {
            duration: 8000,
          }
        );
      }
    } else {
      resetCaptcha();
      setError(result.error?.message || 'An error occurred');
      setLoading(false);
      setLoadingText('Loading...');
    }
  };

  return (
    <div className="mx-auto w-full max-w-md rounded-md border border-gray-25 p-[25px]">
      <div className="mb-6 flex justify-center">
        <GeoforgeLogoWithText />
      </div>

      <div className="mb-6 text-center">
        <h1 className="mb-4 text-xl font-bold text-black">Sign Up</h1>
        <p className="text-sm text-blue-25">Welcome! Please create an account to continue</p>
      </div>

      <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-2">
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            className="mt-1.5"
            type="email"
            placeholder="user@example.com"
            id="email"
            name="email"
            defaultValue={emailParam || ''}
            required
          />
        </div>

        <PasswordInput
          label="Password"
          name="password"
          id="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />

        <PasswordInput
          label="Confirm Password"
          name="confirmPassword"
          id="confirmPassword"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          required
        />

        {confirmPassword && (
          <p
            className={`mt-1 text-sm ${
              password === confirmPassword ? 'text-green-600' : 'text-red-500'
            }`}
          >
            {password === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
          </p>
        )}

        <CaptchaWidget ref={widgetRef} />

        <Button
          type="submit"
          isLoading={loading}
          disabled={confirmPassword.length > 0 && password !== confirmPassword}
          loadingText={loadingText}
          className="mt-2 w-full bg-black"
        >
          Sign Up
        </Button>

        {error && <SignupErrorMessage message={error} />}
      </form>

      {APP_SETTINGS.ENABLE_GOOGLE_AUTH && (
        <>
          <AuthDivider />
          <GoogleAuthButton mode="signup" />
        </>
      )}

      <div className="mt-6 text-center text-sm text-defaultFont">
        <span>Already have an account? </span>
        <Link
          href={buildUrlWithReturnToParam('/login')}
          className="font-bold text-defaultFont hover:text-defaultFont/80"
        >
          Sign in
        </Link>
        <span> here.</span>
      </div>
    </div>
  );
};

export default SignUpForm;
